/**
 * Electron API Adapter
 * Bridge entre la API plana del desktop-app antiguo (LegacyElectronAPI)
 * y la API modular que espera el frontend (ElectronAPI)
 *
 * Permite que el frontend funcione tanto con el desktop-app antiguo
 * (API plana) como con el nuevo sippar-electron (API modular)
 */

import type { ElectronAPI } from '../types/electron/electron-api.types';
import type { LegacyElectronAPI } from '../types/electron/legacy-api.types';
import type { WindowAPI, ChildWindowOptions, CreateWindowResult, ChildWindowInfo, OpenAuthWindowOptions } from '../types/electron/window.types';
import type { AppAPI } from '../types/electron/app.types';
import type { UpdatesAPI, UpdateCheckResult } from '../types/electron/updates.types';
import type { ScreenCaptureAPI, DesktopSource } from '../types/electron/screen-capture.types';
import type { UtilsAPI, AuthWindowResult } from '../types/electron/utils.types';
import type { StorageAPI, StorageResult, UserProfilesMap, StoredUserProfile, UserProfileListResult, OfflineValidityResult } from '../types/electron/storage.types';
import type { NetworkAPI, NetworkModeResult, NetworkMode } from '../types/electron/network.types';
import type { HostAPI, OpenProgramResult, KillResult, PowerResult, PowerOptions } from '../types/electron/host.types';
import type { CryptoAPI, HashResult, ValidateResult, PasswordRequirementsResult } from '../types/electron/crypto.types';
import type { ScreenshotAPI, ScreenshotResult, ScreenshotMetadata, ScreenshotListResult, ScreenshotFullResult, ArchiveListResult, SaveScreenshotInput } from '../types/electron/screenshot.types';
import type { UsbDeviceAPI } from '../types/electron/usb-device.types';
import { PowerAction } from '../types/electron/host.types';

// ========== Detection ==========

/**
 * Detecta si la API de Electron es la versión legacy (plana)
 * La API modular tiene sub-objetos (window, app, etc.)
 * La API legacy tiene métodos directos (minimize, getAppVersion, etc.)
 */
export function isLegacyApi(api: unknown): api is LegacyElectronAPI {
  if (!api || typeof api !== 'object') return false;

  const obj = api as Record<string, unknown>;

  // La API legacy tiene 'getAppVersion' como método directo
  // La API modular tiene 'app' como sub-objeto con 'getVersion'
  const hasLegacyMethods = typeof obj.getAppVersion === 'function'
    && typeof obj.minimize === 'function';

  const hasModularStructure = obj.app !== null
    && typeof obj.app === 'object'
    && typeof (obj.app as Record<string, unknown>).getVersion === 'function';

  // Es legacy si tiene los métodos planos Y no tiene estructura modular
  return hasLegacyMethods && !hasModularStructure;
}

/**
 * Detecta si la API de Electron es la versión modular
 */
export function isModularApi(api: unknown): api is ElectronAPI {
  if (!api || typeof api !== 'object') return false;

  const obj = api as Record<string, unknown>;

  return obj.app !== null
    && typeof obj.app === 'object'
    && typeof (obj.app as Record<string, unknown>).getVersion === 'function'
    && obj.window !== null
    && typeof obj.window === 'object';
}

// ========== Stub Helpers ==========

const LEGACY_NOT_AVAILABLE = 'Not available in legacy desktop app. Update to the latest version.';

function notAvailableError<T>(moduleName: string, methodName: string): Promise<T> {
  console.warn(`[ElectronAdapter] ${moduleName}.${methodName}() ${LEGACY_NOT_AVAILABLE}`);
  return Promise.reject(new Error(`${moduleName}.${methodName}: ${LEGACY_NOT_AVAILABLE}`));
}

function notAvailableResult<T>(moduleName: string, methodName: string): Promise<StorageResult<T>> {
  console.warn(`[ElectronAdapter] ${moduleName}.${methodName}() ${LEGACY_NOT_AVAILABLE}`);
  return Promise.resolve({ success: false, error: `${moduleName}.${methodName}: ${LEGACY_NOT_AVAILABLE}` });
}

// ========== Module Adapters ==========

function createAppAdapter(legacy: LegacyElectronAPI): AppAPI {
  return {
    getVersion: () => legacy.getAppVersion(),
    quit: () => legacy.close().then(() => { }) ?? Promise.resolve(),
  };
}

function createWindowAdapter(legacy: LegacyElectronAPI): WindowAPI {
  return {
    createChild: (options: ChildWindowOptions): Promise<CreateWindowResult> => {
      if (options.url) {
        return legacy.openWindow(options.url)
          .then(() => ({ success: true } as CreateWindowResult))
          .catch((err: Error) => ({ success: false, error: err.message }));
      }
      return Promise.resolve({ success: false, error: 'URL is required for legacy window creation' });
    },
    close: (_windowId?: string) =>
      legacy.close().then(() => true).catch(() => false) ?? Promise.resolve(true),
    minimize: () =>
      legacy.minimize().then(() => true).catch(() => false) ?? Promise.resolve(true),
    maximize: () => {
      console.warn('[ElectronAdapter] window.maximize() not available in legacy API');
      return Promise.resolve(false);
    },
    toggleFullscreen: () => {
      console.warn('[ElectronAdapter] window.toggleFullscreen() not available in legacy API');
      return Promise.resolve(false);
    },
    getChildren: () => Promise.resolve([] as ChildWindowInfo[]),
    openWindowAuth: (_options: OpenAuthWindowOptions): Promise<AuthWindowResult> => {
      console.warn('[ElectronAdapter] window.openWindowAuth() not available in legacy API');
      return Promise.resolve({ success: false, error: LEGACY_NOT_AVAILABLE });
    },
  };
}

function createUpdatesAdapter(legacy: LegacyElectronAPI): UpdatesAPI {
  return {
    check: (): Promise<UpdateCheckResult> =>
      legacy.startAutoUpdate()
        .then((res: any) => ({
          updateAvailable: res?.status === 'downloading-started',
          version: res?.version,
        } as UpdateCheckResult))
        .catch(() => ({ updateAvailable: false })),
    download: () => legacy.startAutoUpdate(),
    install: () => legacy.installUpdate(),
    cancelDownload: () => legacy.cancelUpdate(),
    onDownloadProgress: (_callback: (progress: number) => void) => {
      // La API legacy usa polling con getUpdateProgress() en vez de callbacks
      // No hay forma directa de suscribirse a eventos de progreso
      console.warn('[ElectronAdapter] updates.onDownloadProgress() limited in legacy API — uses polling');
      return () => { /* no-op cleanup */ };
    },
    onUpdateStatus: (_callback: (data: { status: string; version?: string; error?: string }) => void) => {
      // La API legacy no emite eventos de estado de actualización
      console.warn('[ElectronAdapter] updates.onUpdateStatus() not available in legacy API');
      return () => { /* no-op cleanup */ };
    },
  };
}

function createScreenCaptureAdapter(legacy: LegacyElectronAPI): ScreenCaptureAPI {
  return {
    getSources: () =>
      legacy.getSources().then((sources) =>
        (sources as DesktopSource[]) ?? []
      ),
    setSource: (sourceId: string) =>
      legacy.getSource(sourceId).then(() => { }),
    getSourceStream: (sourceId: string) =>
      legacy.getSourceStream(sourceId) as Promise<MediaStream>,
    onSetSource: (_callback: (sourceId: string) => void) => {
      // SET_SOURCE se maneja via ipcRenderer.on en el preload,
      // no hay forma de suscribirse desde el renderer en la API legacy
      console.warn('[ElectronAdapter] screenCapture.onSetSource() not available in legacy API');
      return () => { /* no-op cleanup */ };
    },
  };
}

function createUtilsAdapter(legacy: LegacyElectronAPI): UtilsAPI {
  return {
    reload: () => legacy.reload().then(() => { }) ?? Promise.resolve(),
    openAuthWindow: (_url: string): Promise<AuthWindowResult> => {
      console.warn('[ElectronAdapter] utils.openAuthWindow() not available in legacy API');
      return Promise.resolve({ success: false, error: LEGACY_NOT_AVAILABLE });
    },
  };
}

function createHostAdapter(legacy: LegacyElectronAPI): HostAPI {
  const wrapProgramResult = (result: unknown): OpenProgramResult => {
    if (result === false) return { success: false, error: 'Program execution failed' };
    return { success: true };
  };

  const wrapKillResult = (processName: string, result: unknown): KillResult => {
    if (result === false) return { success: false, processName, error: 'Kill failed' };
    return { success: true, processName };
  };

  const createPowerResult = (action: PowerAction, success: boolean, error?: string): PowerResult => ({
    success,
    action,
    error,
    supported: true,
  });

  return {
    openProgram: (path?: string) =>
      Promise.resolve(wrapProgramResult(legacy.abrirPrograma(path))),
    killProcess: (_processName: string) =>
      notAvailableError<KillResult>('host', 'killProcess'),
    closeProgram: (programPath?: string) =>
      Promise.resolve(wrapKillResult(programPath ?? '', legacy.cerrarPrograma(programPath))),
    killMultipleProcesses: (_processNames: string[]) =>
      notAvailableError<Record<string, KillResult>>('host', 'killMultipleProcesses'),
    isAppAvailable: (path?: string) => legacy.isAppAvailable(path),
    checkMultiplePaths: (_paths: string[]) =>
      notAvailableError<Record<string, boolean>>('host', 'checkMultiplePaths'),
    executePowerAction: (action: PowerAction, _options?: PowerOptions) => {
      if (action === PowerAction.SHUTDOWN) {
        const result = legacy.shutdownPC();
        return Promise.resolve(createPowerResult(action, result !== false));
      }
      return Promise.resolve(createPowerResult(action, false, `${action} not available in legacy API`));
    },
    shutdown: (_options?: PowerOptions) => {
      const result = legacy.shutdownPC();
      return Promise.resolve(createPowerResult(PowerAction.SHUTDOWN, result !== false));
    },
    restart: (_options?: PowerOptions) =>
      Promise.resolve(createPowerResult(PowerAction.RESTART, false, LEGACY_NOT_AVAILABLE)),
    logout: (_options?: PowerOptions) =>
      Promise.resolve(createPowerResult(PowerAction.LOGOUT, false, LEGACY_NOT_AVAILABLE)),
    sleep: (_options?: PowerOptions) =>
      Promise.resolve(createPowerResult(PowerAction.SLEEP, false, LEGACY_NOT_AVAILABLE)),
    hibernate: (_options?: PowerOptions) =>
      Promise.resolve(createPowerResult(PowerAction.HIBERNATE, false, LEGACY_NOT_AVAILABLE)),
    cancelScheduledShutdown: () => Promise.resolve(false),
    isComitasNetAppAvailable: () => legacy.isAppAvailable(),
    openComitasNetApp: () =>
      Promise.resolve(wrapProgramResult(legacy.openConnApp())),
  };
}

function createStorageStub(): StorageAPI {
  return {
    set: <T>(_key: string, _value: T) => notAvailableResult<void>('storage', 'set'),
    get: <T>(_key: string) => notAvailableResult<T>('storage', 'get'),
    delete: (_key: string) => notAvailableResult<void>('storage', 'delete'),
    clear: () => notAvailableResult<void>('storage', 'clear'),
    isEncryptionAvailable: () => Promise.resolve(false),
    saveProfile: (_user) => notAvailableResult<void>('storage', 'saveProfile'),
    getProfile: (_userId: string) => notAvailableResult<StoredUserProfile>('storage', 'getProfile'),
    getAllProfiles: () => notAvailableResult<UserProfilesMap>('storage', 'getAllProfiles'),
    deleteProfile: (_userId: string) => notAvailableResult<void>('storage', 'deleteProfile'),
    listProfiles: (): Promise<UserProfileListResult> =>
      Promise.resolve({ success: false, error: `storage.listProfiles: ${LEGACY_NOT_AVAILABLE}` }),
    markOfflineUsage: (_userId: string) => notAvailableResult<void>('storage', 'markOfflineUsage'),
    resetOfflineUsage: (_userId: string) => notAvailableResult<void>('storage', 'resetOfflineUsage'),
    checkOfflineValidity: (_userId: string): Promise<OfflineValidityResult> =>
      Promise.resolve({ isValid: false, reason: 'not_found', error: LEGACY_NOT_AVAILABLE }),
    cleanupExpiredProfiles: () => notAvailableResult<{ deletedCount: number }>('storage', 'cleanupExpiredProfiles'),
  };
}

function createNetworkStub(): NetworkAPI {
  return {
    getMode: (): Promise<NetworkModeResult> =>
      Promise.resolve({ success: true, mode: 'online' as NetworkMode }),
    setMode: (_mode: NetworkMode) => notAvailableResult<void>('network', 'setMode'),
    setModeWithUser: (_mode: NetworkMode, _userId?: string) => notAvailableResult<void>('network', 'setModeWithUser'),
    consumePendingAutoLogin: () => notAvailableResult<string>('network', 'consumePendingAutoLogin'),
    getDefaultGateway: () => Promise.resolve({ success: false, error: `network.getDefaultGateway: ${LEGACY_NOT_AVAILABLE}` }),
  };
}

function createCryptoStub(): CryptoAPI {
  return {
    hash: (_signature: string): Promise<HashResult> =>
      Promise.resolve({ success: false, error: `crypto.hash: ${LEGACY_NOT_AVAILABLE}` }),
    validate: (_signature: string, _hashedSignature: string): Promise<ValidateResult> =>
      Promise.resolve({ success: false, isValid: false, error: `crypto.validate: ${LEGACY_NOT_AVAILABLE}` }),
    hashSync: (_signature: string): Promise<HashResult> =>
      Promise.resolve({ success: false, error: `crypto.hashSync: ${LEGACY_NOT_AVAILABLE}` }),
    validateSync: (_signature: string, _hashedSignature: string): Promise<ValidateResult> =>
      Promise.resolve({ success: false, isValid: false, error: `crypto.validateSync: ${LEGACY_NOT_AVAILABLE}` }),
    checkPasswordRequirements: (_password: string): Promise<PasswordRequirementsResult> =>
      Promise.resolve({ success: false, isValid: false, error: `crypto.checkPasswordRequirements: ${LEGACY_NOT_AVAILABLE}` }),
  };
}

function createScreenshotStub(): ScreenshotAPI {
  return {
    save: (_input: SaveScreenshotInput): Promise<ScreenshotResult<ScreenshotMetadata>> =>
      Promise.resolve({ success: false, error: `screenshot.save: ${LEGACY_NOT_AVAILABLE}` }),
    list: (_userId: string): Promise<ScreenshotListResult> =>
      Promise.resolve({ success: false, error: `screenshot.list: ${LEGACY_NOT_AVAILABLE}` }),
    get: (_userId: string, _screenshotId: string): Promise<ScreenshotFullResult> =>
      Promise.resolve({ success: false, error: `screenshot.get: ${LEGACY_NOT_AVAILABLE}` }),
    delete: (_userId: string, _screenshotId: string): Promise<ScreenshotResult<void>> =>
      Promise.resolve({ success: false, error: `screenshot.delete: ${LEGACY_NOT_AVAILABLE}` }),
    cleanup: (): Promise<ScreenshotResult<{ archivedCount: number; deletedArchives: number }>> =>
      Promise.resolve({ success: false, error: `screenshot.cleanup: ${LEGACY_NOT_AVAILABLE}` }),
    listArchives: (_userId: string): Promise<ArchiveListResult> =>
      Promise.resolve({ success: false, error: `screenshot.listArchives: ${LEGACY_NOT_AVAILABLE}` }),
    restore: (_userId: string, _archiveFileName: string): Promise<ScreenshotResult<{ restoredCount: number }>> =>
      Promise.resolve({ success: false, error: `screenshot.restore: ${LEGACY_NOT_AVAILABLE}` }),
  };
}

// ========== Main Adapter ==========

function createUsbDeviceStub(): UsbDeviceAPI {
  return {
    initMonitoring: () => Promise.resolve(),
    getConnected: () => Promise.resolve([]),
    getDebugInfo: () => Promise.resolve({
      webUsbAvailable: false,
      authorizedDevices: [],
      monitoredDevices: [],
      connectedDevices: [],
    }),
    simulate: () => Promise.resolve({
      applied: false,
      connected: false,
      reason: 'USB device API not available in this environment',
    }),
    onChange: () => () => { /* noop */ },
  };
}

// ========== Main Adapter ==========

/**
 * Información extra de la API legacy que no encaja en módulos
 */
export interface LegacyExtras {
  /** Token de autenticación de Comitas (propiedad estática del preload) */
  comitasToken: string;
  /** Plataforma del sistema (process.platform) */
  platform: string;
}

/**
 * Adapta la API plana del desktop-app antiguo al formato modular
 * que espera el frontend
 *
 * @param legacy - API plana del desktop-app
 * @returns API modular compatible con ElectronAPI
 */
export function adaptLegacyApi(legacy: LegacyElectronAPI): ElectronAPI {
  return {
    app: createAppAdapter(legacy),
    window: createWindowAdapter(legacy),
    updates: createUpdatesAdapter(legacy),
    screenCapture: createScreenCaptureAdapter(legacy),
    utils: createUtilsAdapter(legacy),
    host: createHostAdapter(legacy),
    // Módulos sin equivalente en la API legacy — devuelven stubs controlados
    storage: createStorageStub(),
    network: createNetworkStub(),
    crypto: createCryptoStub(),
    screenshot: createScreenshotStub(),
    usbDevice: createUsbDeviceStub(),
  };
}

/**
 * Extrae las propiedades extra de la API legacy (comitasToken, platform)
 */
export function extractLegacyExtras(legacy: LegacyElectronAPI): LegacyExtras {
  return {
    comitasToken: legacy.comitasToken ?? '',
    platform: legacy.platform ?? '',
  };
}

/**
 * Normaliza la API de Electron: si es legacy la adapta, si es modular la devuelve tal cual
 *
 * @param api - API de Electron (puede ser legacy o modular)
 * @returns { electron, isLegacy, extras }
 */
export function normalizeElectronApi(api: ElectronAPI | LegacyElectronAPI): {
  electron: ElectronAPI;
  isLegacy: boolean;
  extras: LegacyExtras | null;
} {
  if (isLegacyApi(api)) {
    console.info('[ElectronAdapter] Legacy desktop-app API detected — applying adapter bridge');
    return {
      electron: adaptLegacyApi(api),
      isLegacy: true,
      extras: extractLegacyExtras(api),
    };
  }

  // Es modular o al menos no es legacy — asumir modular
  return {
    electron: api as ElectronAPI,
    isLegacy: false,
    extras: null,
  };
}
